import { TimeagoCustomFormatter, TimeagoFormatter, TimeagoIntl } from "ngx-timeago";
// Import all regular language strings
import { strings as afStrings } from "ngx-timeago/language-strings/af";
import { strings as arStrings } from "ngx-timeago/language-strings/ar";
import { strings as azStrings } from "ngx-timeago/language-strings/az";
import { strings as azShortStrings } from "ngx-timeago/language-strings/az-short";
import { strings as bgStrings } from "ngx-timeago/language-strings/bg";
import { strings as bsStrings } from "ngx-timeago/language-strings/bs";
import { strings as caStrings } from "ngx-timeago/language-strings/ca";
import { strings as csStrings } from "ngx-timeago/language-strings/cs";
import { strings as cyStrings } from "ngx-timeago/language-strings/cy";
import { strings as daStrings } from "ngx-timeago/language-strings/da";
import { strings as deStrings } from "ngx-timeago/language-strings/de";
import { strings as deShortStrings } from "ngx-timeago/language-strings/de-short";
import { strings as dvStrings } from "ngx-timeago/language-strings/dv";
import { strings as elStrings } from "ngx-timeago/language-strings/el";
import { strings as enStrings } from "ngx-timeago/language-strings/en";
import { strings as enShortStrings } from "ngx-timeago/language-strings/en-short";
import { strings as esStrings } from "ngx-timeago/language-strings/es";
import { strings as esShortStrings } from "ngx-timeago/language-strings/es-short";
import { strings as etStrings } from "ngx-timeago/language-strings/et";
import { strings as euStrings } from "ngx-timeago/language-strings/eu";
import { strings as faStrings } from "ngx-timeago/language-strings/fa";
import { strings as faShortStrings } from "ngx-timeago/language-strings/fa-short";
import { strings as fiStrings } from "ngx-timeago/language-strings/fi";
import { strings as frStrings } from "ngx-timeago/language-strings/fr";
import { strings as frShortStrings } from "ngx-timeago/language-strings/fr-short";
import { strings as glStrings } from "ngx-timeago/language-strings/gl";
import { strings as heStrings } from "ngx-timeago/language-strings/he";
import { strings as hrStrings } from "ngx-timeago/language-strings/hr";
import { strings as huStrings } from "ngx-timeago/language-strings/hu";
import { strings as hyStrings } from "ngx-timeago/language-strings/hy";
import { strings as idStrings } from "ngx-timeago/language-strings/id";
import { strings as isStrings } from "ngx-timeago/language-strings/is";
import { strings as itStrings } from "ngx-timeago/language-strings/it";
import { strings as itShortStrings } from "ngx-timeago/language-strings/it-short";
import { strings as jaStrings } from "ngx-timeago/language-strings/ja";
import { strings as jvStrings } from "ngx-timeago/language-strings/jv";
import { strings as koStrings } from "ngx-timeago/language-strings/ko";
import { strings as kyStrings } from "ngx-timeago/language-strings/ky";
import { strings as ltStrings } from "ngx-timeago/language-strings/lt";
import { strings as lvStrings } from "ngx-timeago/language-strings/lv";
import { strings as mkStrings } from "ngx-timeago/language-strings/mk";
import { strings as nlStrings } from "ngx-timeago/language-strings/nl";
import { strings as noStrings } from "ngx-timeago/language-strings/no";
import { strings as plStrings } from "ngx-timeago/language-strings/pl";
import { strings as ptStrings } from "ngx-timeago/language-strings/pt";
import { strings as ptBrStrings } from "ngx-timeago/language-strings/pt-br";
import { strings as ptBrShortStrings } from "ngx-timeago/language-strings/pt-br-short";
import { strings as ptShortStrings } from "ngx-timeago/language-strings/pt-short";
import { strings as roStrings } from "ngx-timeago/language-strings/ro";
import { strings as rsStrings } from "ngx-timeago/language-strings/rs";
import { strings as ruStrings } from "ngx-timeago/language-strings/ru";
import { strings as rwStrings } from "ngx-timeago/language-strings/rw";
import { strings as siStrings } from "ngx-timeago/language-strings/si";
import { strings as skStrings } from "ngx-timeago/language-strings/sk";
import { strings as slStrings } from "ngx-timeago/language-strings/sl";
import { strings as srStrings } from "ngx-timeago/language-strings/sr";
import { strings as svStrings } from "ngx-timeago/language-strings/sv";
import { strings as thStrings } from "ngx-timeago/language-strings/th";
import { strings as trStrings } from "ngx-timeago/language-strings/tr";
import { strings as trShortStrings } from "ngx-timeago/language-strings/tr-short";
import { strings as ukStrings } from "ngx-timeago/language-strings/uk";
import { strings as uzStrings } from "ngx-timeago/language-strings/uz";
import { strings as viStrings } from "ngx-timeago/language-strings/vi";
import { strings as zhCNStrings } from "ngx-timeago/language-strings/zh-CN"; // Simplified Chinese (zh-hans in AstroBin)
import { strings as zhTWStrings } from "ngx-timeago/language-strings/zh-TW"; // Traditional Chinese (zh-hant in AstroBin)

export function timeagoIntlFactory(strings: any): () => TimeagoIntl {
  return () => {
    const intl = new TimeagoIntl();
    intl.strings = strings;
    intl.changes.next();
    return intl;
  };
}

export function timeagoFormatterFactory(intlToken: any): any {
  return {
    provide: TimeagoFormatter,
    useFactory: (intl: TimeagoIntl) => new TimeagoCustomFormatter(intl),
    deps: [intlToken]
  };
}

// Helper function to create provider pairs (intl and formatter)
function createProviderPair(langCode: string, format: string, strings: any) {
  const tokenName = `TIMEAGO_${langCode.toUpperCase()}_${format.toUpperCase()}`;
  return [{ provide: tokenName, useFactory: timeagoIntlFactory(strings) }, timeagoFormatterFactory(tokenName)];
}

// Create an array of all language providers
const languageProviders = [
  // Default provider
  { provide: TimeagoIntl, useFactory: timeagoIntlFactory(enStrings) },

  // Regular language providers
  ...createProviderPair("af", "long", afStrings),
  ...createProviderPair("ar", "long", arStrings),
  ...createProviderPair("az", "long", azStrings),
  ...createProviderPair("az", "short", azShortStrings),
  ...createProviderPair("bg", "long", bgStrings),
  ...createProviderPair("bs", "long", bsStrings),
  ...createProviderPair("ca", "long", caStrings),
  ...createProviderPair("cs", "long", csStrings),
  ...createProviderPair("cy", "long", cyStrings),
  ...createProviderPair("da", "long", daStrings),
  ...createProviderPair("de", "long", deStrings),
  ...createProviderPair("de", "short", deShortStrings),
  ...createProviderPair("dv", "long", dvStrings),
  ...createProviderPair("el", "long", elStrings),
  ...createProviderPair("en", "long", enStrings),
  ...createProviderPair("en", "short", enShortStrings),
  ...createProviderPair("es", "long", esStrings),
  ...createProviderPair("es", "short", esShortStrings),
  ...createProviderPair("et", "long", etStrings),
  ...createProviderPair("eu", "long", euStrings),
  ...createProviderPair("fa", "long", faStrings),
  ...createProviderPair("fa", "short", faShortStrings),
  ...createProviderPair("fi", "long", fiStrings),
  ...createProviderPair("fr", "long", frStrings),
  ...createProviderPair("fr", "short", frShortStrings),
  ...createProviderPair("gl", "long", glStrings),
  ...createProviderPair("he", "long", heStrings),
  ...createProviderPair("hr", "long", hrStrings),
  ...createProviderPair("hu", "long", huStrings),
  ...createProviderPair("hy", "long", hyStrings),
  ...createProviderPair("id", "long", idStrings),
  ...createProviderPair("is", "long", isStrings),
  ...createProviderPair("it", "long", itStrings),
  ...createProviderPair("it", "short", itShortStrings),
  ...createProviderPair("ja", "long", jaStrings),
  ...createProviderPair("jv", "long", jvStrings),
  ...createProviderPair("ko", "long", koStrings),
  ...createProviderPair("ky", "long", kyStrings),
  ...createProviderPair("lt", "long", ltStrings),
  ...createProviderPair("lv", "long", lvStrings),
  ...createProviderPair("mk", "long", mkStrings),
  ...createProviderPair("nl", "long", nlStrings),
  ...createProviderPair("no", "long", noStrings),
  ...createProviderPair("pl", "long", plStrings),
  ...createProviderPair("pt", "long", ptStrings),
  ...createProviderPair("pt", "short", ptShortStrings),
  ...createProviderPair("pt-br", "long", ptBrStrings),
  ...createProviderPair("pt-br", "short", ptBrShortStrings),
  ...createProviderPair("ro", "long", roStrings),
  ...createProviderPair("rs", "long", rsStrings),
  ...createProviderPair("ru", "long", ruStrings),
  ...createProviderPair("rw", "long", rwStrings),
  ...createProviderPair("si", "long", siStrings),
  ...createProviderPair("sk", "long", skStrings),
  ...createProviderPair("sl", "long", slStrings),
  ...createProviderPair("sr", "long", srStrings),
  ...createProviderPair("sv", "long", svStrings),
  ...createProviderPair("th", "long", thStrings),
  ...createProviderPair("tr", "long", trStrings),
  ...createProviderPair("tr", "short", trShortStrings),
  ...createProviderPair("uk", "long", ukStrings),
  ...createProviderPair("uz", "long", uzStrings),
  ...createProviderPair("vi", "long", viStrings),
  ...createProviderPair("zh-CN", "long", zhCNStrings),
  ...createProviderPair("zh-TW", "long", zhTWStrings)
];

export const TIMEAGO_PROVIDERS = languageProviders;
